import type {
  TApiApplicationsResponse,
  TApiApplicationDetails,
  TApiApplicationDocumentsResponse,
  TApiContragent,
  TApiSupervisorContragentBlocksResponse,
  TApiCovenantsResponse,
  TApiReadyCovenantsResponse,
  TApiRequiredDocumentsResponse,
  TApiSupervisorSegment,
  TApiSupervisorApplication,
  TApiSupervisorApplicationDocumentsResponse,
  TApiSupervisorApplicationsResponse,
  TApplicationStatus,
} from '~/modules/covenants/types.ts';

export enum FetchStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

export type TSupervisorApplicationAssignmentRequest = {
  applicationId: string;
  version: number;
};

export type TAcceptSupervisorApplicationRequest = {
  applicationId: string;
  version: number;
  payload: {
    accept: {
      documents: Array<{
        id: string;
      }>;
    };
    deny: {
      documents: Array<{
        id: string;
        comment: string;
      }>;
    };
  };
};

export type TSupervisorApplicationDocumentsRequest = {
  applicationId: string;
  page?: number;
  size?: number;
};

export type TUpdateSupervisorApplicationDocumentFileCommentRequest = {
  applicationId: string;
  fileId: string | number;
  comment: string;
};

export type TUpdateSupervisorApplicationDocumentsCommentRequest = {
  applicationId: string;
  comment: string;
};

export type TSupervisorApplicationRequest = {
  applicationId: string;
};

export type TSupervisorApplicationReadyCovenantsRequest = {
  applicationId: string;
  page?: number;
  size?: number;
};

export type TSupervisorApplicationReadyCovenantDocumentsRequest = {
  applicationId: string;
  documentIds: number[];
  yearPeriod?: string;
  childPeriod?: string;
  deadline?: string;
};

export type TCreateSupervisorSegmentRequest = {
  segmentId: string;
  email: string;
};

export type TDeleteSupervisorSegmentSupervisorRequest = {
  segmentId: string;
  supervisorId: string;
};

export type TCertificateGenerationType =
  | 'stateAidReceived'
  | 'financialCondition'
  | 'incomeStructureByKved';

export enum CertificateGenerationDocumentId {
  FinancialCondition = '5',
  StateAidReceived = '10',
  IncomeStructureByKved = '13',
}

export type TFinancialConditionCertificateGenerationResponse = {
  id: number;
  fileId: number;
};

export type TStateAidCertificateGenerationResponse = {
  id: number;
  fileId: number;
};

export type TIncomeStructureByKvedCertificateGenerationResponse = {
  id: number;
  fileId: number;
};

export type TThunkError = {
  message: string;
  status?: number;
  errorCode?: string;
};

export type TAsyncSlice<T> = {
  data: T;
  status: FetchStatus;
  error: TThunkError | null;
};

export type TCertificateGenerationState = {
  stateAidReceived: TAsyncSlice<TStateAidCertificateGenerationResponse | null>;
  financialCondition: TAsyncSlice<TFinancialConditionCertificateGenerationResponse | null>;
  incomeStructureByKved: TAsyncSlice<TIncomeStructureByKvedCertificateGenerationResponse | null>;
};

export type TDocumentGenerationBank = {
  id: number;
  fullName?: string | null;
  name?: string | null;
  shortname?: string | null;
  sname?: string | null;
  kb?: number | string | null;
  idNbu?: number | string | null;
};

export type TDocumentGenerationBanksResponse = {
  content: TDocumentGenerationBank[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
  last: boolean;
};

export type TDocumentGenerationCurrency = {
  id: number;
  name: string;
  sname?: string | null;
  symbolCode?: string | null;
};

export type TSupervisorDocumentTemplate = {
  id: number;
  name: string;
};

export type TCovenantsAccessDeniedModal = {
  open: boolean;
  message: string;
  redirectPath: string | null;
};

export type TSupervisorApplicationsRequest = {
  page: number;
  size: number;
  fromDate: string;
  toDate: string;
  search?: string;
  assignee?: string[];
  segmentId?: string[];
  status?: TApplicationStatus[];
  sort?: string[];
  documentIds?: string[];
  yearPeriod?: string;
  childPeriod?: TSupervisorApplicationChildPeriod;
  startDeadLine?: string;
  endDealLine?: string;
};

export type TSupervisorApplicationChildPeriod =
  | 'JANUARY'
  | 'FEBRUARY'
  | 'MARCH'
  | 'APRIL'
  | 'MAY'
  | 'JUNE'
  | 'JULY'
  | 'AUGUST'
  | 'SEPTEMBER'
  | 'OCTOBER'
  | 'NOVEMBER'
  | 'DECEMBER'
  | 'Q1'
  | 'Q2'
  | 'Q3'
  | 'Q4';

export type TFetchCallCenterOperatorContragentsRequest = {
  code: string;
};

export type TFetchSupervisorContragentBlocksRequest = {
  contragentId: number;
  page: number;
  size: number;
  sort?: string[];
};

export type TCreateSupervisorContragentBlockRequest = {
  contragentId: number;
  email: string;
};

export type TDeleteSupervisorContragentBlockRequest = {
  contragentId: number;
  email: string;
};

export type TCovenantsPageStore = {
  contragents: TAsyncSlice<TApiContragent[]>;
  currentContragent: TAsyncSlice<TApiContragent | null>;
  supervisorContragentBlocks: TAsyncSlice<TApiSupervisorContragentBlocksResponse>;
  requiredDocuments: TAsyncSlice<TApiRequiredDocumentsResponse>;
  covenants: TAsyncSlice<TApiCovenantsResponse>;
  applications: TAsyncSlice<TApiApplicationsResponse>;
  activeApplications: TAsyncSlice<TApiApplicationsResponse>;
  supervisorSegments: TAsyncSlice<TApiSupervisorSegment[]>;
  supervisorApplications: TAsyncSlice<TApiSupervisorApplicationsResponse>;
  supervisorReadyCovenants: TAsyncSlice<TApiReadyCovenantsResponse>;
  currentSupervisorApplication: TAsyncSlice<TApiSupervisorApplication | null>;
  supervisorApplicationDocuments: TAsyncSlice<TApiSupervisorApplicationDocumentsResponse>;
  currentApplication: TAsyncSlice<TApiApplicationDetails | null>;
  applicationDocuments: TAsyncSlice<TApiApplicationDocumentsResponse>;
  applicationUnsignedDocumentsCount: TAsyncSlice<number | null>;
  documentGenerationBanks: TAsyncSlice<TDocumentGenerationBank[]>;
  documentGenerationCurrencies: TAsyncSlice<TDocumentGenerationCurrency[]>;
  supervisorDocumentTemplates: TAsyncSlice<TSupervisorDocumentTemplate[]>;
  certificateGeneration: TCertificateGenerationState;
  saveSignedDocumentsFromDiia: {
    status: FetchStatus;
    error: string | null;
  };
  accessDeniedModal: TCovenantsAccessDeniedModal;
};
